import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import pickingVentaService, {
  type PickingAccionPendiente,
  type PickingItem,
  type PickingListoResult,
  type PickingPedidoDetalle,
  type PickingPedidoPendiente,
} from '../../services/pickingVentaService';
import pedidoVentaService from '../../services/pedidoVentaService';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatClienteConNro, formatCurrency, formatDate } from '../../utils/formatters';
import { facturaPosPath } from '../../config/routes';
import { useFormHotkeys } from '../../hooks/useFormHotkeys';
import Can from '../../components/Can';
import { useCan } from '../../hooks/useCan';
import PDFViewer from '../../components/shared/PDFViewer';
import PickingItemsTable from './components/PickingItemsTable';
import PickingDeshacerDialog from './components/PickingDeshacerDialog';
import PickingEventosDialog from './components/PickingEventosDialog';
import { PickingLecturaErrorDialog, PickingListoResultadoDialog, PickingParcialDialog } from './components/PickingListoDialogs';
import {
  formatCantidadPicking,
  formatComprobantePicking,
  lecturaRequiereAceptacion,
  listoRequiereDecisionPendiente,
  mensajeAlertaLectura,
  mensajeListoPendiente,
} from './pickingHelpers';

interface UltimaLectura {
  ok: boolean;
  mensaje: string;
  codigoArticulo?: string | null;
}

const PENDIENTES_REFRESH_MS = 60000;

const cardSx = {
  bgcolor: '#ffffff',
  borderRadius: 3,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  display: 'flex',
  flexDirection: 'column',
  height: { xs: 'auto', lg: 'calc(100vh - 220px)' },
  minHeight: { lg: 520 },
};
const cardContentSx = { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' };
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } };
const headSx = { fontWeight: 600, bgcolor: '#f5f5f5' };

function parseCantidad(raw: string): number | undefined {
  const n = Number(raw.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

const PickingMasivo: React.FC = () => {
  const navigate = useNavigate();
  const puedeRegistrar = useCan('ventas.picking-masivo.nuevo');
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const pedidoId = Number(searchParams.get('pedido')) || null;

  const [busqueda, setBusqueda] = useState('');
  const [pendientes, setPendientes] = useState<PickingPedidoPendiente[]>([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);

  const [detalle, setDetalle] = useState<PickingPedidoDetalle | null>(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const [codigo, setCodigo] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [lecturasEnCurso, setLecturasEnCurso] = useState(0);
  const [ultimaLectura, setUltimaLectura] = useState<UltimaLectura | null>(null);
  const [errorLectura, setErrorLectura] = useState<string | null>(null);

  const [deshacerItem, setDeshacerItem] = useState<PickingItem | null>(null);
  const [deshacerLoading, setDeshacerLoading] = useState(false);
  const [eventosOpen, setEventosOpen] = useState(false);
  const [parcialOpen, setParcialOpen] = useState(false);
  const [parcialMensaje, setParcialMensaje] = useState<string | null>(null);
  const [listoLoading, setListoLoading] = useState(false);
  const [resultado, setResultado] = useState<PickingListoResult | null>(null);
  const [resultadoComprobante, setResultadoComprobante] = useState('');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const codigoInputRef = useRef<HTMLInputElement>(null);
  const lecturaQueueRef = useRef<Promise<void>>(Promise.resolve());
  const lecturaBloqueadaRef = useRef(false);
  const pedidoIdRef = useRef(pedidoId);
  pedidoIdRef.current = pedidoId;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const focusCodigo = useCallback(() => {
    window.setTimeout(() => codigoInputRef.current?.focus(), 0);
  }, []);

  const cargarPendientes = useCallback(async (term: string) => {
    setLoadingPendientes(true);
    try {
      setPendientes(await pickingVentaService.getPendientes({ busqueda: term }));
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar los pedidos pendientes de picking'), {
        variant: 'error',
      });
    } finally {
      setLoadingPendientes(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    const timer = window.setTimeout(() => void cargarPendientes(busqueda), 300);
    const interval = window.setInterval(() => void cargarPendientes(busqueda), PENDIENTES_REFRESH_MS);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
    };
  }, [busqueda, cargarPendientes]);

  useEffect(() => {
    setUltimaLectura(null);
    setCodigo('');
    setCantidad('');
    setErrorLectura(null);
    setParcialOpen(false);
    setParcialMensaje(null);
    lecturaBloqueadaRef.current = false;
    if (!pedidoId) {
      setDetalle(null);
      return undefined;
    }
    let cancelado = false;
    setLoadingDetalle(true);
    pickingVentaService
      .getDetalle(pedidoId)
      .then((data) => {
        if (cancelado) return;
        setDetalle(data);
        focusCodigo();
      })
      .catch((error: unknown) => {
        if (cancelado) return;
        setDetalle(null);
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudo abrir el pedido para pickear'), { variant: 'error' });
        setSearchParamsRef.current({}, { replace: true });
      })
      .finally(() => {
        if (!cancelado) setLoadingDetalle(false);
      });
    return () => {
      cancelado = true;
    };
  }, [pedidoId, enqueueSnackbar, focusCodigo]);

  const seleccionarPedido = (id: number) => {
    if (id === pedidoId) {
      focusCodigo();
      return;
    }
    setSearchParams({ pedido: String(id) }, { replace: true });
  };

  const limpiarSeleccion = () => {
    setSearchParams({}, { replace: true });
  };

  const liberarLector = () => {
    lecturaBloqueadaRef.current = false;
    setCodigo('');
    setCantidad('');
    focusCodigo();
  };

  const aceptarErrorLectura = () => {
    setErrorLectura(null);
    liberarLector();
  };

  const leerCodigo = () => {
    if (!puedeRegistrar) return;
    if (lecturaBloqueadaRef.current) {
      setCodigo('');
      setCantidad('');
      return;
    }
    const code = codigo.trim();
    const id = pedidoId;
    if (!code || !id) return;
    const cant = parseCantidad(cantidad);
    setCodigo('');
    setCantidad('');
    setLecturasEnCurso((n) => n + 1);

    lecturaQueueRef.current = lecturaQueueRef.current.then(async () => {
      try {
        if (lecturaBloqueadaRef.current || pedidoIdRef.current !== id) return;
        const r = await pickingVentaService.leer(id, code, cant);
        if (lecturaBloqueadaRef.current || pedidoIdRef.current !== id) return;

        if (r.pedido) setDetalle(r.pedido);

        if (lecturaRequiereAceptacion(r)) {
          lecturaBloqueadaRef.current = true;
          setCodigo('');
          setCantidad('');
          setUltimaLectura(null);
          setErrorLectura(mensajeAlertaLectura(r, code));
          return;
        }

        setUltimaLectura({
          ok: true,
          codigoArticulo: r.codigoArticulo,
          mensaje: `${r.codigoArticulo ?? code}  ${r.descripcion ?? ''}  +${formatCantidadPicking(r.cantidadAplicada)}  →  ${formatCantidadPicking(r.cantidadPickeada)} / ${formatCantidadPicking(r.cantidadPedida)}`,
        });
      } catch (error: unknown) {
        if (pedidoIdRef.current !== id) return;
        lecturaBloqueadaRef.current = true;
        setCodigo('');
        setCantidad('');
        setUltimaLectura(null);
        setErrorLectura(`${code}: ${getApiErrorMessage(error, 'Código no válido para este pedido')}`);
      } finally {
        setLecturasEnCurso((n) => Math.max(0, n - 1));
      }
    });
  };

  const confirmarDeshacer = async (item: PickingItem, cant: number) => {
    if (!puedeRegistrar || !pedidoId || !item.codigoArticulo) return;
    setDeshacerLoading(true);
    try {
      await lecturaQueueRef.current;
      setDetalle(await pickingVentaService.deshacer(pedidoId, item.codigoArticulo, cant));
      setUltimaLectura(null);
      setDeshacerItem(null);
      enqueueSnackbar(`Se deshicieron ${formatCantidadPicking(cant)} de ${item.codigoArticulo}`, { variant: 'info' });
      focusCodigo();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo deshacer la lectura'), { variant: 'error' });
    } finally {
      setDeshacerLoading(false);
    }
  };

  const itemsPendientes = useMemo(
    () => (detalle?.items ?? []).filter((i) => i.cantidadPendiente > 0),
    [detalle],
  );

  const abrirDecisionPendiente = (mensaje?: string | null) => {
    lecturaBloqueadaRef.current = true;
    setCodigo('');
    setCantidad('');
    setParcialMensaje(mensaje ?? null);
    setParcialOpen(true);
  };

  const finalizarListoOk = (r: PickingListoResult, comprobante: string) => {
    setParcialOpen(false);
    setParcialMensaje(null);
    lecturaBloqueadaRef.current = false;
    setResultadoComprobante(comprobante);
    setResultado(r);
    limpiarSeleccion();
    void cargarPendientes(busqueda);
  };

  const ejecutarListo = async (opts: {
    confirmarParcial?: boolean;
    accionPendiente?: PickingAccionPendiente | null;
  } = {}) => {
    if (!pedidoId || !detalle) return;
    setListoLoading(true);
    const comprobante = formatComprobantePicking(detalle);
    try {
      await lecturaQueueRef.current;
      const r = await pickingVentaService.listo(pedidoId, {
        confirmarParcial: opts.confirmarParcial,
        accionPendiente: opts.accionPendiente,
      });

      if (listoRequiereDecisionPendiente(r) && !opts.accionPendiente) {
        abrirDecisionPendiente(mensajeListoPendiente(r));
        return;
      }

      finalizarListoOk(r, comprobante);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo finalizar el picking'), { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setListoLoading(false);
    }
  };

  const handleListo = () => {
    if (!puedeRegistrar || !detalle || listoLoading) return;
    if (lecturasEnCurso > 0) {
      enqueueSnackbar('Esperá a que terminen de procesarse las lecturas', { variant: 'info' });
      return;
    }
    if (detalle.totalPickeado <= 0) {
      enqueueSnackbar('Todavía no se pickeó ningún artículo de este pedido', { variant: 'warning' });
      return;
    }
    if (!detalle.completo) {
      abrirDecisionPendiente(null);
      return;
    }
    void ejecutarListo({ confirmarParcial: false });
  };

  const { confirmDialog } = useFormHotkeys({
    hotkeys: { F2: { handler: handleListo, enabled: puedeRegistrar && Boolean(detalle) && !listoLoading } },
    confirmEscape: false,
  });

  const handleImprimir = async () => {
    if (!pedidoId) return;
    setPdfLoading(true);
    try {
      const blob = await pedidoVentaService.getPedidoVentaPDF(pedidoId);
      setPdfUrl(window.URL.createObjectURL(blob));
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al generar el PDF del pedido'), { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const cerrarPdf = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    focusCodigo();
  };

  const lectorBloqueado = Boolean(errorLectura) || parcialOpen;
  const progreso = detalle && detalle.totalPedido > 0
    ? Math.min(100, (detalle.totalPickeado / detalle.totalPedido) * 100)
    : 0;
  const comprobanteActual = detalle ? formatComprobantePicking(detalle) : '';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', maxWidth: '100%', overflowX: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
          Picking Masivo
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={4}>
          <Card sx={cardSx}>
            <CardContent sx={cardContentSx}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Pedidos pendientes de pickear
                </Typography>
                <Tooltip title="Actualizar">
                  <span>
                    <IconButton onClick={() => void cargarPendientes(busqueda)} disabled={loadingPendientes}>
                      {loadingPendientes ? <CircularProgress size={20} /> : <RefreshIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por cliente o número..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                sx={{ ...inputSx, mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TableContainer sx={{ maxHeight: { xs: 300, lg: 'none' }, flex: { lg: 1 }, minHeight: 0 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Fecha</TableCell>
                      <TableCell sx={headSx}>Comprobante</TableCell>
                      <TableCell sx={headSx}>Cliente</TableCell>
                      <TableCell sx={headSx} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendientes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            {loadingPendientes ? 'Cargando...' : 'No hay pedidos enviados a picking.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendientes.map((p) => (
                        <TableRow
                          key={p.nroFormulario}
                          hover
                          selected={p.nroFormulario === pedidoId}
                          onClick={() => seleccionarPedido(p.nroFormulario)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(p.fechaEmision)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatComprobantePicking(p)}
                            {p.pickingIniciado && (
                              <Chip label="En curso" size="small" color="warning" sx={{ ml: 1, height: 20 }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{formatClienteConNro(p.nroCliente, p.nombre)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {formatCurrency(p.importeTotal ?? 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Card sx={cardSx}>
            <CardContent sx={cardContentSx}>
              {loadingDetalle ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : !detalle ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <QrCodeScannerIcon sx={{ fontSize: 64, mb: 1, color: '#bdbdbd' }} />
                  <Typography>Seleccioná un pedido de la lista para empezar a pickear.</Typography>
                </Box>
              ) : (
                <>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    sx={{ mb: 2 }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatClienteConNro(detalle.nroCliente, detalle.nombre)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pedido {comprobanteActual} · {formatDate(detalle.fechaEmision)} · {formatCurrency(detalle.importeTotal ?? 0)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={() => setEventosOpen(true)}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                      >
                        Eventos
                      </Button>
                      <Can permiso="ventas.pedidos.imprimir">
                        <Button
                          variant="outlined"
                          startIcon={pdfLoading ? <CircularProgress size={16} /> : <PrintIcon />}
                          onClick={() => void handleImprimir()}
                          disabled={pdfLoading}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          Imprimir
                        </Button>
                      </Can>
                      <Can permiso="ventas.picking-masivo.nuevo">
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={listoLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        onClick={handleListo}
                        disabled={listoLoading}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                      >
                        F2 - Listo
                      </Button>
                      </Can>
                    </Stack>
                  </Stack>

                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Pickeado {formatCantidadPicking(detalle.totalPickeado)} de {formatCantidadPicking(detalle.totalPedido)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: detalle.completo ? 'success.main' : 'text.primary' }}>
                        {detalle.completo ? 'Completo' : `Faltan ${formatCantidadPicking(detalle.totalPendiente)}`}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={progreso}
                      color={detalle.completo ? 'success' : 'primary'}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      inputRef={codigoInputRef}
                      fullWidth
                      autoComplete="off"
                      label={detalle.modoCodigoBarra ? 'Código de barras' : 'Código de artículo'}
                      value={codigo}
                      disabled={!puedeRegistrar || lectorBloqueado}
                      onChange={(e) => setCodigo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          leerCodigo();
                        }
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#E8F5E9' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QrCodeScannerIcon />
                          </InputAdornment>
                        ),
                        endAdornment: lecturasEnCurso > 0 ? (
                          <InputAdornment position="end">
                            <CircularProgress size={18} />
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                    <TextField
                      label="Cantidad"
                      placeholder="1"
                      type="number"
                      value={cantidad}
                      disabled={!puedeRegistrar || lectorBloqueado}
                      onChange={(e) => setCantidad(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          codigoInputRef.current?.focus();
                        }
                      }}
                      sx={{ ...inputSx, width: { xs: '100%', sm: 140 } }}
                      inputProps={{ min: 0, step: 'any' }}
                    />
                    <Can permiso="ventas.picking-masivo.nuevo">
                    <Button
                      variant="contained"
                      onClick={leerCodigo}
                      disabled={lectorBloqueado || !codigo.trim()}
                      sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
                    >
                      Leer
                    </Button>
                    </Can>
                  </Stack>

                  <Box sx={{ minHeight: 48, mb: 1 }}>
                    {ultimaLectura && (
                      <Alert severity={ultimaLectura.ok ? 'success' : 'error'} sx={{ fontWeight: 600, py: 0 }}>
                        {ultimaLectura.mensaje}
                      </Alert>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <PickingItemsTable
                      items={detalle.items ?? []}
                      ultimoCodigo={ultimaLectura?.ok ? ultimaLectura.codigoArticulo : null}
                      disabled={deshacerLoading || listoLoading || !puedeRegistrar}
                      onDeshacer={setDeshacerItem}
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PickingDeshacerDialog
        item={deshacerItem}
        loading={deshacerLoading}
        onClose={() => {
          setDeshacerItem(null);
          focusCodigo();
        }}
        onConfirm={(item, cant) => void confirmarDeshacer(item, cant)}
      />
      <PickingEventosDialog
        pedidoId={eventosOpen ? pedidoId : null}
        titulo={comprobanteActual}
        onClose={() => {
          setEventosOpen(false);
          focusCodigo();
        }}
      />
      <PickingParcialDialog
        open={parcialOpen}
        pendientes={itemsPendientes}
        loading={listoLoading}
        mensaje={parcialMensaje}
        onCancelar={() => void ejecutarListo({ confirmarParcial: false, accionPendiente: 'Cancelar' })}
        onEnEspera={() => void ejecutarListo({ confirmarParcial: true, accionPendiente: 'EnEspera' })}
      />
      <PickingLecturaErrorDialog mensaje={errorLectura} onAceptar={aceptarErrorLectura} />
      <PickingListoResultadoDialog
        resultado={resultado}
        comprobante={resultadoComprobante}
        onClose={() => setResultado(null)}
        onFacturar={(id) => navigate(facturaPosPath({ pedidoId: id }))}
      />
      {pdfUrl && pedidoId && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={cerrarPdf}
          title={`Pedido ${comprobanteActual}`}
          documentId={`${pedidoId}`}
          documentType="pedido"
        />
      )}
      {confirmDialog}
    </Box>
  );
};

export default PickingMasivo;
